import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Package, Boxes } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyPlan = { name: "", tagline: "", price: "", cost: "", billing: "one-time", popular: false, features: "", exclusive: "" };
const emptyAddon = { name: "", category: "Web Development", price: "", cost: "", billing: "one-time", features: "" };
const CATEGORIES = ["Web Development", "SEO", "Social Media"];

function MarginPill({ value, pct }) {
  const positive = value >= 0;
  return (
    <span className={`font-mono text-xs px-2 py-0.5 border ${positive ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
      {fmt(value)} · {pct}%
    </span>
  );
}

export default function CatalogManager() {
  const navigate = useNavigate();
  const [data, setData] = useState({ packages: [], addons: [] });
  const [loading, setLoading] = useState(true);

  const [planDialog, setPlanDialog] = useState(false);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [planEditId, setPlanEditId] = useState(null);

  const [addonDialog, setAddonDialog] = useState(false);
  const [addonForm, setAddonForm] = useState(emptyAddon);
  const [addonEditId, setAddonEditId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null); // {type, id, name}
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/catalog").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load catalog")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // ---- Plan dialog ----
  const openNewPlan = () => { setPlanForm(emptyPlan); setPlanEditId(null); setPlanDialog(true); };
  const openEditPlan = (p) => {
    setPlanForm({ name: p.name, tagline: p.tagline || "", price: p.price, cost: p.cost ?? "", billing: p.billing, popular: !!p.popular, features: (p.features || []).join("\n"), exclusive: p.exclusive || "" });
    setPlanEditId(p.id); setPlanDialog(true);
  };
  const savePlan = async () => {
    if (!planForm.name || planForm.price === "") { toast.error("Name and price are required"); return; }
    setSaving(true);
    const body = {
      name: planForm.name, tagline: planForm.tagline,
      price: parseFloat(planForm.price) || 0, cost: parseFloat(planForm.cost) || 0,
      billing: planForm.billing, popular: planForm.popular,
      features: planForm.features.split("\n").map((s) => s.trim()).filter(Boolean),
      exclusive: planForm.exclusive,
    };
    try {
      if (planEditId) await api.put(`/admin/packages/${planEditId}`, body);
      else await api.post("/admin/packages", body);
      toast.success(planEditId ? "Plan updated" : "Plan added");
      setPlanDialog(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // ---- Addon dialog ----
  const openNewAddon = () => { setAddonForm(emptyAddon); setAddonEditId(null); setAddonDialog(true); };
  const openEditAddon = (a) => {
    setAddonForm({ name: a.name, category: a.category, price: a.price, cost: a.cost ?? "", billing: a.billing, features: (a.features || []).join("\n") });
    setAddonEditId(a.id); setAddonDialog(true);
  };
  const saveAddon = async () => {
    if (!addonForm.name || addonForm.price === "") { toast.error("Name and price are required"); return; }
    setSaving(true);
    const body = {
      name: addonForm.name, category: addonForm.category,
      price: parseFloat(addonForm.price) || 0, cost: parseFloat(addonForm.cost) || 0,
      billing: addonForm.billing,
      features: addonForm.features.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (addonEditId) await api.put(`/admin/addons/${addonEditId}`, body);
      else await api.post("/admin/addons", body);
      toast.success(addonEditId ? "Product updated" : "Product added");
      setAddonDialog(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const { type, id } = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/admin/${type === "plan" ? "packages" : "addons"}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="catalog-manager-page">
      <button onClick={() => navigate("/admin")} className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Catalog</span>
      <h1 className="mt-2 font-display font-black text-4xl tracking-tighter mb-8">Plans & Products</h1>

      {/* PLANS */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-2xl tracking-tight">Plans</h2>
          </div>
          <Button onClick={openNewPlan} data-testid="add-plan-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs">
            <Plus className="h-4 w-4 mr-1" /> Add Plan
          </Button>
        </div>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Billing</th>
                <th className="text-right px-4 py-3">Cost</th>
                <th className="text-right px-4 py-3">Selling Price</th>
                <th className="text-right px-4 py-3">Margin</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.packages.map((p) => (
                <tr key={p.id} data-testid={`plan-row-${p.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{p.name} {p.popular && <span className="font-mono text-[9px] uppercase bg-primary text-white px-1.5 py-0.5 ml-1">Popular</span>}</p>
                    <p className="text-xs text-zinc-500">{p.tagline}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{p.billing}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500">{fmt(p.cost)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmt(p.price)}</td>
                  <td className="px-4 py-3 text-right"><MarginPill value={p.margin} pct={p.margin_pct} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`edit-plan-${p.id}`} onClick={() => openEditPlan(p)} className="rounded-none h-8 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-plan-${p.id}`} onClick={() => setDeleteTarget({ type: "plan", id: p.id, name: p.name })} className="rounded-none h-8 px-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && data.packages.length === 0 && <p className="p-8 text-center text-zinc-500">No plans yet.</p>}
        </div>
      </section>

      {/* PRODUCTS & SERVICES */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-2xl tracking-tight">Products & Services</h2>
          </div>
          <Button onClick={openNewAddon} data-testid="add-product-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs">
            <Plus className="h-4 w-4 mr-1" /> Add Product
          </Button>
        </div>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="text-left px-4 py-3">Product / Service</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Billing</th>
                <th className="text-right px-4 py-3">Cost</th>
                <th className="text-right px-4 py-3">Selling Price</th>
                <th className="text-right px-4 py-3">Margin</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.addons.map((a) => (
                <tr key={a.id} data-testid={`product-row-${a.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium">
                    {a.name}
                    {a.features && a.features.length > 0 && (
                      <p className="text-xs text-zinc-500 font-normal mt-0.5">{a.features.join(" · ")}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{a.category}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{a.billing}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500">{fmt(a.cost)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmt(a.price)}</td>
                  <td className="px-4 py-3 text-right"><MarginPill value={a.margin} pct={a.margin_pct} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`edit-product-${a.id}`} onClick={() => openEditAddon(a)} className="rounded-none h-8 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-product-${a.id}`} onClick={() => setDeleteTarget({ type: "product", id: a.id, name: a.name })} className="rounded-none h-8 px-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && data.addons.length === 0 && <p className="p-8 text-center text-zinc-500">No products yet.</p>}
        </div>
      </section>

      {/* PLAN DIALOG */}
      <Dialog open={planDialog} onOpenChange={setPlanDialog}>
        <DialogContent className="rounded-none max-w-lg" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-display">{planEditId ? "Edit Plan" : "Add Plan"}</DialogTitle>
            <DialogDescription>Set the plan name, internal cost, and customer-facing selling price.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Name"><Input data-testid="plan-name-input" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} className="rounded-none" /></Field>
            <Field label="Tagline"><Input value={planForm.tagline} onChange={(e) => setPlanForm({ ...planForm, tagline: e.target.value })} className="rounded-none" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Our Cost ($)"><Input data-testid="plan-cost-input" type="number" value={planForm.cost} onChange={(e) => setPlanForm({ ...planForm, cost: e.target.value })} className="rounded-none" /></Field>
              <Field label="Selling Price ($)"><Input data-testid="plan-price-input" type="number" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} className="rounded-none" /></Field>
            </div>
            <Field label="Billing">
              <Select value={planForm.billing} onValueChange={(v) => setPlanForm({ ...planForm, billing: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one-time">One-time</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Features (one per line)"><Textarea value={planForm.features} onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })} className="rounded-none min-h-[90px]" /></Field>
            <Field label="Exclusive highlight"><Input value={planForm.exclusive} onChange={(e) => setPlanForm({ ...planForm, exclusive: e.target.value })} className="rounded-none" /></Field>
            <label className="flex items-center gap-2 text-sm"><Switch checked={planForm.popular} onCheckedChange={(v) => setPlanForm({ ...planForm, popular: v })} /> Mark as "Most Popular"</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(false)} className="rounded-none">Cancel</Button>
            <Button onClick={savePlan} disabled={saving} data-testid="save-plan-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white">{saving ? "Saving…" : "Save Plan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADDON DIALOG */}
      <Dialog open={addonDialog} onOpenChange={setAddonDialog}>
        <DialogContent className="rounded-none max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-display">{addonEditId ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription>Set the product/service name, internal cost, and selling price.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Name"><Input data-testid="product-name-input" value={addonForm.name} onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })} className="rounded-none" /></Field>
            <Field label="Category">
              <Select value={addonForm.category} onValueChange={(v) => setAddonForm({ ...addonForm, category: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Our Cost ($)"><Input data-testid="product-cost-input" type="number" value={addonForm.cost} onChange={(e) => setAddonForm({ ...addonForm, cost: e.target.value })} className="rounded-none" /></Field>
              <Field label="Selling Price ($)"><Input data-testid="product-price-input" type="number" value={addonForm.price} onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value })} className="rounded-none" /></Field>
            </div>
            <Field label="Billing">
              <Select value={addonForm.billing} onValueChange={(v) => setAddonForm({ ...addonForm, billing: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one-time">One-time</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Features (one per line)"><Textarea data-testid="product-features-input" value={addonForm.features} onChange={(e) => setAddonForm({ ...addonForm, features: e.target.value })} className="rounded-none min-h-[80px]" placeholder={"e.g.\n12 posts / month\nHashtag research\nMonthly report"} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddonDialog(false)} className="rounded-none">Cancel</Button>
            <Button onClick={saveAddon} disabled={saving} data-testid="save-product-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white">{saving ? "Saving…" : "Save Product"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This will remove it from the catalog. Existing quotes keep their saved prices.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-catalog-delete-btn" onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
